import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./styles/UploadDocuments.css";
import Icons from "../components/icons";
import profileimage from "../assets/profileimage.png";
import useOnboarding from "../hooks/useOnboarding";
import { authFetchFormData } from "../lib/api";

// ─── Document Card Component ─────────────────────────────────────────────────

function DocumentCard({
  id,
  label,
  subLabel,
  hint,
  icon,
  iconBg,
  file,
  onChange,
  required,
}) {
  const inputRef = useRef(null);

  const handleClick = () => inputRef.current?.click();

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) onChange(id, selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) onChange(id, dropped);
  };

  const handleDragOver = (e) => e.preventDefault();

  return (
    <div className="doc-card">
      {/* Header row */}
      <div className="doc-card__header">
        <div className="doc-card__icon-wrap" style={{ background: iconBg }}>
          {icon}
        </div>
        <div className="doc-card__meta">
          <p className="doc-card__label">
            {label}
            {required && <span className="doc-card__required"> *</span>}
          </p>
          <p className="doc-card__sublabel">{subLabel}</p>
        </div>
      </div>

      {/* Upload zone */}
      <div
        className={`doc-card__dropzone${file ? " doc-card__dropzone--filled" : ""}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleClick()}
        aria-label={`Upload ${label}`}
      >
        {file ? (
          <div className="doc-card__file-info">
            <Icons.UploadIcon />
            <span className="doc-card__filename">{file.name}</span>
          </div>
        ) : (
          <div className="doc-card__upload-prompt">
            <Icons.UploadIcon />
            <span>Upload Document</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="doc-card__input"
          onChange={handleFileChange}
        />
      </div>

      {/* Footer hints */}
      <div className="doc-card__footer">
        <span>Upload JPG/PNG</span>
        <span>Max Size 2Mb</span>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function UploadDocuments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Admins have no document-verification step. SetUpProfile already routes
  // them past this page, but guard the route itself so reaching it directly
  // (back button, pasted URL) doesn't strand them on a form they must not
  // fill in.
  const { role, loadInfo } = useOnboarding();
  const isAdmin = String(role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (token && !role) loadInfo(token);
  }, [token, role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin && token) {
      navigate(`/set-password?token=${encodeURIComponent(token)}`, {
        replace: true,
      });
    }
  }, [isAdmin, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const [files, setFiles] = useState({
    aadhaar: null,
    pan: null,
    experienceLetter: null,
    marksCard: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
    setError(null);
  };

  const validate = () => {
    if (!files.aadhaar || !files.pan) {
      setError("Aadhaar and PAN documents are required.");
      return false;
    }
    if (!files.experienceLetter && !files.marksCard) {
      setError("Please upload either an experience letter or marks card.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("aadhaar", files.aadhaar);
      formData.append("pan", files.pan);
      if (files.experienceLetter)
        formData.append("experienceLetter", files.experienceLetter);
      if (files.marksCard) formData.append("marksCard", files.marksCard);

      // Use the shared helper rather than a hand-rolled fetch: it resolves the
      // API base the same way as the rest of the app (own-origin "/api" when
      // deployed) and attaches the auth token. The previous hardcoded
      // "http://localhost:5000/api" fallback pointed at the *viewer's* machine
      // once deployed, so document upload failed for every invited user.
      const res = await authFetchFormData("/api/auth/onboarding/documents", {
        method: "POST",
        body: formData,
        // Checked by the server before any file is accepted.
        headers: { "X-Invite-Token": token },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.message ||
            "One or more documents were rejected. Please ensure files are valid JPG, PNG, or PDF under 2MB.",
        );
      }

      setSuccess(true);
      // Navigate to set-password step after a brief delay
      setTimeout(() => navigate(`/set-password?token=${token}`), 1000);
    } catch (err) {
      if (err.message === "Failed to fetch") {
        setError(
          "Unable to reach the server. Please check your connection and try again.",
        );
      } else {
        setError(
          err.message ||
            "Document upload failed. Please check your files and try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-left-panel">
        <img src={profileimage} alt="Profile" className="upload-left-image" />
      </div>
      {/* Right panel */}
      <div className="upload-panel">
        {/* Logo */}
        <div className="upload-panel__logo">
          <Icons.Logo />
          <span>QwickHub</span>
        </div>

        {/* Title */}
        <h1 className="upload-panel__title">Upload Documents</h1>

        {/* Compulsory Documents */}
        <section className="upload-section">
          <h2 className="upload-section__label">Compulsory Documents</h2>
          <div className="upload-section__grid">
            <DocumentCard
              id="aadhaar"
              label="Aadhaar Card"
              subLabel="Upload clear front or e-Aadhaar Document"
              icon={<Icons.AadhaarIcon />}
              iconBg="#D2E0FB"
              file={files.aadhaar}
              onChange={handleFileChange}
              required
            />
            <DocumentCard
              id="pan"
              label="PAN Card"
              subLabel="Upload clear front of PAN Card"
              icon={<Icons.PanIcon />}
              iconBg="#D2E0FB"
              file={files.pan}
              onChange={handleFileChange}
              required
            />
          </div>
        </section>

        {/* Experience / Education */}
        <section className="upload-section">
          <h2 className="upload-section__label">
            Experience /Education Proof(Any One Required)
          </h2>
          <div className="upload-section__grid">
            <DocumentCard
              id="experienceLetter"
              label="Experience Certificate"
              subLabel="Upload experience certificate from previous employer"
              icon={<Icons.BriefcaseIcon />}
              iconBg="#CFFFDE"
              file={files.experienceLetter}
              onChange={handleFileChange}
            />
            <DocumentCard
              id="marksCard"
              label="Marks Card"
              subLabel="Upload latest Marks Card"
              icon={<Icons.GraduationIcon />}
              iconBg="#C6A6F8"
              file={files.marksCard}
              onChange={handleFileChange}
            />
          </div>
        </section>

        {/* Error message */}
        {error && <p className="upload-panel__error">{error}</p>}
        {success && (
          <p className="upload-panel__success">
            Documents uploaded! Redirecting…
          </p>
        )}

        {/* Submit */}
        <button
          className="upload-panel__submit"
          onClick={handleSubmit}
          disabled={isLoading || success}
        >
          {isLoading ? "Uploading…" : "Submit"}
        </button>

        {/* Footer */}
        <p className="upload-panel__footer">
          © QwickBit Technologies Pvt Ltd , 2026
        </p>
      </div>
    </div>
  );
}
